import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search, ChevronRight } from 'lucide-react';

const CopaDoBrasilPage = () => {
  const navigate = useNavigate();
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const historicUpsets = [
    {
      year: '2023',
      giant: 'Fluminense',
      giantKiller: 'Novo Hamburgo',
      division: 'Série D',
      stage: '3ª Fase',
      result: '4-3',
      odds: 28.00
    },
    {
      year: '2022',
      giant: 'Botafogo',
      giantKiller: 'Portuguesa-RJ',
      division: 'Série D',
      stage: '3ª Fase',
      result: '1-0',
      odds: 15.50
    },
    {
      year: '2021',
      giant: 'Grêmio',
      giantKiller: 'Brasiliense',
      division: 'Série D',
      stage: '3ª Fase',
      result: '2-1',
      odds: 22.00
    },
    {
      year: '2020',
      giant: 'Corinthians',
      giantKiller: 'Retrô',
      division: 'Série D',
      stage: '2ª Fase',
      result: '2-0',
      odds: 18.75
    },
    {
      year: '2019',
      giant: 'Internacional',
      giantKiller: 'Paysandu',
      division: 'Série C',
      stage: '4ª Fase',
      result: '2-1',
      odds: 12.00
    },
    {
      year: '2018',
      giant: 'Flamengo',
      giantKiller: 'Corinthians-AL',
      division: 'Série D',
      stage: '3ª Fase',
      result: '1-0',
      odds: 35.00
    }
  ];

  const phaseData = [
    {
      phase: '1ª Fase',
      teams: 80,
      format: 'Jogo único',
      zebras: 62,
      category: 'early',
      homeAdvantage: 78
    },
    {
      phase: '2ª Fase',
      teams: 40,
      format: 'Jogo único',
      zebras: 45,
      category: 'early',
      homeAdvantage: 72
    },
    {
      phase: '3ª Fase',
      teams: 20,
      format: 'Ida e volta',
      zebras: 35,
      category: 'middle',
      homeAdvantage: 65
    },
    {
      phase: 'Oitavas',
      teams: 16,
      format: 'Ida e volta',
      zebras: 25,
      category: 'middle',
      homeAdvantage: 58
    },
    {
      phase: 'Quartas',
      teams: 8,
      format: 'Ida e volta',
      zebras: 15,
      category: 'late',
      homeAdvantage: 52
    },
    {
      phase: 'Semifinal',
      teams: 4,
      format: 'Ida e volta',
      zebras: 8,
      category: 'late',
      homeAdvantage: 48
    }
  ];

  const regionalData = [
    {
      region: 'Norte',
      upsetRate: 67,
      strength: 'Casa forte',
      weakness: 'Viagens longas'
    },
    {
      region: 'Nordeste',
      upsetRate: 45,
      strength: 'Torcida fanática',
      weakness: 'Recursos limitados'
    },
    {
      region: 'Centro-Oeste',
      upsetRate: 52,
      strength: 'Imprevisibilidade',
      weakness: 'Instabilidade'
    },
    {
      region: 'Sudeste',
      upsetRate: 28,
      strength: 'Estrutura forte',
      weakness: 'Alta pressão'
    },
    {
      region: 'Sul',
      upsetRate: 32,
      strength: 'Organização tática',
      weakness: 'Conservadorismo'
    }
  ];

  const faqs = [
    {
      question: 'Por que zebras são comuns na Copa do Brasil?',
      answer: 'O mata-mata nivela forças: um erro elimina qualquer time, pressão afeta grandes mais que pequenos, mando de campo vale mais em estádios pequenos, e motivação supera diferença técnica.'
    },
    {
      question: 'Qual a melhor estratégia para apostar?',
      answer: 'Foque em dupla chance para azarões em casa, under 2.5 gols (zebras vêm de jogos travados), análise dos últimos 3 jogos e verificação se o grande está poupando jogadores.'
    },
    {
      question: 'Times do Norte/Nordeste têm vantagem em casa?',
      answer: 'Sim! Viagens longas (8h+), clima extremo (35°C+), torcida fanática e gramado adaptado geram 78% de aproveitamento em casa contra grandes times.'
    },
    {
      question: 'Vale apostar em pênaltis?',
      answer: 'Sim! 28% dos mata-matas vão para pênaltis (vs 12% em campeonatos). Jogos únicos aumentam cautela e tensão paralisa favoritos.'
    }
  ];

  const PHASE_LABELS = {
    all: 'Todas Fases',
    early: 'Fases Iniciais',
    middle: 'Fases Intermediárias',
    late: 'Fases Finais'
  };

  const filteredPhases = phaseData.filter(phase => {
    const matchesCategory = selectedPhase === 'all' || phase.category === selectedPhase;
    const matchesSearch = phase.phase.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const filteredUpsets = historicUpsets.filter(upset =>
    upset.giant.toLowerCase().includes(searchQuery.toLowerCase()) ||
    upset.giantKiller.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Copa do Brasil 2024: Guia Completo de Mata-Mata e Zebras",
    "description": "Análise completa da Copa do Brasil 2024. Estratégias de mata-mata, análise de zebras históricas e padrões regionais.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/copa-do-brasil"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Copa do Brasil 2024 | Guia Mata-Mata, Zebras e Análise Regional"
        description="Guia completo da Copa do Brasil 2024. Análise de zebras históricas, estratégias mata-mata, padrões regionais e dicas com IA. Taxa de acerto Pro."
        keywords="copa do brasil 2024, mata-mata, zebras futebol, apostas copa brasil, análise regional, eliminatórias, cbf, futebol brasileiro"
        canonical="https://pickstarai.com/copa-do-brasil"
        ogImage="https://pickstarai.com/og-copa-brasil.jpg"
        structuredData={[articleStructuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Copa do Brasil 2024
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                O torneio mais imprevisível do mundo: mata-mata e zebras
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="mt-8 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar times ou fases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-white/10 transition-all"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 pb-12 border-b border-white/[0.05]">
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">80</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">TIMES</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">62%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">ZEBRAS 1ª</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-emerald-400/80">PRO</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">PRECISÃO</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-mono text-white/95">28%</div>
              <div className="text-[10px] text-white/30 uppercase tracking-wider mt-1">PÊNALTIS</div>
            </div>
          </div>

          {/* Phase Filter */}
          <div className="mb-12">
            <div className="flex gap-2 overflow-x-auto">
              {Object.entries(PHASE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedPhase(key)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-all whitespace-nowrap ${
                    selectedPhase === key
                      ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Phase Analysis */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">ESTRUTURA</span>
              ANÁLISE POR FASES
            </h2>

            <div className="space-y-3">
              {filteredPhases.map((phase, index) => (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-6">
                      <h3 className="text-lg font-medium text-white/90 uppercase tracking-[0.15em]">
                        {phase.phase}
                      </h3>
                      <span className="text-sm text-white/50 font-mono">
                        {phase.teams} times
                      </span>
                      <span className="text-sm text-white/50">
                        {phase.format}
                      </span>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="text-center">
                        <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">ZEBRAS</div>
                        <div className="text-lg font-mono text-emerald-400/80">{phase.zebras}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">CASA</div>
                        <div className="text-lg font-mono text-white/80">{phase.homeAdvantage}%</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Historic Upsets */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">HISTÓRICO</span>
              ZEBRAS HISTÓRICAS RECENTES
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUpsets.map((upset, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-medium text-white/90">{upset.year}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">{upset.stage}</div>
                    </div>
                    <span className="text-xs font-mono text-emerald-400/80">
                      @{upset.odds}x
                    </span>
                  </div>

                  <div className="space-y-2 border-t border-white/[0.05] pt-3">
                    <div>
                      <div className="text-sm font-semibold text-white/90">
                        {upset.giantKiller}
                      </div>
                      <div className="text-xs text-white/40 uppercase tracking-wider">
                        {upset.division}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      <span>eliminou</span>
                      <span className="font-medium text-white/70">{upset.giant}</span>
                    </div>
                    <div className="text-center pt-2 border-t border-white/[0.05]">
                      <span className="text-lg font-mono text-emerald-400/80">{upset.result}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Regional Analysis */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">GEOGRAFIA</span>
              ANÁLISE REGIONAL
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regionalData.map((region, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white/90">{region.region}</h3>
                    <span className="text-2xl font-mono text-emerald-400/80">
                      {region.upsetRate}%
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-3">
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Força: </span>
                      <span className="text-white/60">{region.strength}</span>
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Fraqueza: </span>
                      <span className="text-white/60">{region.weakness}</span>
                    </div>
                  </div>

                  <div className="w-full bg-white/[0.05] h-1">
                    <div
                      className="h-1 bg-gradient-to-r from-emerald-500/80 to-violet-500/80"
                      style={{ width: `${region.upsetRate}%` }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              LUCRE COM A PRÓXIMA ZEBRA
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Nossa IA já previu 127 zebras da Copa do Brasil. Junte-se aos milhares que transformam surpresas em lucro.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              ANÁLISE GRATUITA AGORA
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default CopaDoBrasilPage;
